if (!customElements.get('yotpo-site-reviews')) {
  customElements.define(
    'yotpo-site-reviews',
    class YotpoSiteReviews extends HTMLElement {
      /* Two independent review streams sharing one Yotpo app key:
       *  - site    : store-wide reviews (v1 widget "yotpo_site_reviews" product)
       *  - product : reviews aggregated across all products (v3 storefront)
       * Each tab keeps its own paging/state so switching never refetches. */
      #tabs = [
        { id: 'site', label: 'Site Reviews' },
        { id: 'product', label: 'Product Reviews' },
      ];

      #active = 'site';

      #state = {
        site: this.#blankTab(),
        product: this.#blankTab(),
      };

      #blankTab() {
        return {
          reviews: [],
          products: {},
          page: 0,
          total: 0,
          bottomline: { averageScore: 0, totalReview: 0, starDistribution: {} },
          loading: false,
          loaded: false,
          error: null,
        };
      }

      get #key() {
        return this.dataset.yotpoKey;
      }

      get #perPage() {
        return parseInt(this.dataset.perPage, 10) || 12;
      }

      get #tab() {
        return this.#state[this.#active];
      }

      /* ---------------------------------------------------------------- */
      /*  Data sources                                                    */
      /* ---------------------------------------------------------------- */
      #buildUrl(id, page) {
        if (id === 'site') {
          return `https://api.yotpo.com/v1/widget/${this.#key}/products/yotpo_site_reviews/reviews.json?page=${page}&per_page=${this.#perPage}`;
        }
        return `https://api-cdn.yotpo.com/v3/storefront/store/${this.#key}/reviews?page=${page}&perPage=${this.#perPage}&sort=date`;
      }

      #normalize(id, data) {
        if (id === 'site') {
          const r = data.response || {};
          const b = r.bottomline || {};
          return {
            reviews: r.reviews || [],
            products: {},
            page: r.pagination?.page ?? 1,
            total: r.pagination?.total ?? 0,
            bottomline: {
              averageScore: b.average_score ?? 0,
              totalReview: b.total_review ?? 0,
              starDistribution: b.star_distribution || {},
            },
          };
        }
        const products = {};
        (data.products || []).forEach((p) => {
          products[p.id] = p;
        });
        const b = data.bottomline || {};
        return {
          reviews: data.reviews || [],
          products,
          page: data.pagination?.page ?? 1,
          total: data.pagination?.total ?? 0,
          bottomline: {
            averageScore: b.averageScore ?? 0,
            totalReview: b.totalReview ?? 0,
            starDistribution: b.starDistribution || {},
          },
        };
      }

      connectedCallback() {
        this.#fetchPage(this.#active, 1);

        this.addEventListener('click', (e) => {
          const tabBtn = e.target.closest('[data-tab]');
          if (tabBtn) {
            e.preventDefault();
            this.#switchTab(tabBtn.dataset.tab);
            return;
          }
          const more = e.target.closest('[data-load-more]');
          if (more) {
            e.preventDefault();
            this.#fetchPage(this.#active, this.#tab.page + 1);
          }
        });
      }

      #switchTab(id) {
        if (id === this.#active || !this.#state[id]) return;
        this.#active = id;
        if (!this.#tab.loaded && !this.#tab.loading) {
          this.#fetchPage(id, 1);
        } else {
          this.#render();
        }
      }

      async #fetchPage(id, page) {
        const tab = this.#state[id];
        tab.loading = true;
        tab.error = null;
        if (this.#active === id) this.#render();

        try {
          const res = await fetch(this.#buildUrl(id, page));
          if (!res.ok) throw new Error('Reviews fetch failed');
          const norm = this.#normalize(id, await res.json());

          tab.products = { ...tab.products, ...norm.products };
          tab.reviews = page === 1 ? norm.reviews : [...tab.reviews, ...norm.reviews];
          tab.page = norm.page || page;
          tab.total = norm.total ?? tab.total;
          tab.bottomline = norm.bottomline;
          tab.loaded = true;
        } catch (err) {
          tab.error = 'Unable to load reviews. Please try again later.';
        } finally {
          tab.loading = false;
          if (this.#active === id) this.#render();
        }
      }

      /* ---------------------------------------------------------------- */
      /*  Helpers                                                         */
      /* ---------------------------------------------------------------- */
      #escape(str) {
        const div = document.createElement('div');
        div.textContent = str == null ? '' : String(str);
        return div.innerHTML;
      }

      #stars(score) {
        const full = Math.floor(score);
        const half = score - full >= 0.5;
        let out = '';
        for (let i = 1; i <= 5; i += 1) {
          if (i <= full) out += this.#starSVG('full');
          else if (i === full + 1 && half) out += this.#starSVG('half');
          else out += this.#starSVG('empty');
        }
        return `<span class="ysr-stars" role="img" aria-label="${score} out of 5 stars">${out}</span>`;
      }

      #starSVG(type) {
        const fill = type === 'empty' ? '#D8D2C4' : '#E8A33D';
        if (type === 'half') {
          return `<svg class="ysr-star" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><defs><linearGradient id="ysr-half"><stop offset="50%" stop-color="#E8A33D"/><stop offset="50%" stop-color="#D8D2C4"/></linearGradient></defs><path fill="url(#ysr-half)" d="M12 2l2.9 6.3 6.9.6-5.2 4.5 1.6 6.7L12 17.3 5.8 20.6l1.6-6.7L2.2 8.9l6.9-.6z"/></svg>`;
        }
        return `<svg class="ysr-star" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="${fill}" d="M12 2l2.9 6.3 6.9.6-5.2 4.5 1.6 6.7L12 17.3 5.8 20.6l1.6-6.7L2.2 8.9l6.9-.6z"/></svg>`;
      }

      #formatDate(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
      }

      #initial(name) {
        const n = (name || '').trim();
        return n ? n.charAt(0).toUpperCase() : '?';
      }

      /* ---------------------------------------------------------------- */
      /*  Render                                                          */
      /* ---------------------------------------------------------------- */
      #render() {
        this.innerHTML = `${this.#renderTabs()}${this.#renderPanel()}`;
      }

      #renderTabs() {
        const buttons = this.#tabs
          .map((t) => {
            const active = t.id === this.#active;
            return `<button type="button" class="ysr-tab${active ? ' ysr-tab--active' : ''}" data-tab="${t.id}" role="tab" aria-selected="${active}">${t.label}</button>`;
          })
          .join('');
        return `<div class="ysr-tabs" role="tablist">${buttons}</div>`;
      }

      #renderPanel() {
        const s = this.#tab;

        if (s.loading && s.reviews.length === 0) {
          return this.#renderSkeleton();
        }
        if (s.error && s.reviews.length === 0) {
          return `<p class="ysr-message">${s.error}</p>`;
        }
        if (!s.loading && s.reviews.length === 0) {
          return `<p class="ysr-message">No reviews yet. Check back soon!</p>`;
        }

        return `
          ${this.#renderSummary()}
          <ul class="ysr-list">
            ${s.reviews.map((r) => this.#renderCard(r)).join('')}
          </ul>
          ${this.#renderLoadMore()}
        `;
      }

      #renderSummary() {
        const b = this.#tab.bottomline;
        const avg = (b.averageScore || 0).toFixed(1);
        const dist = b.starDistribution || {};
        const maxBar = Math.max(1, ...[5, 4, 3, 2, 1].map((n) => dist[n] || 0));
        const bars = [5, 4, 3, 2, 1]
          .map((n) => {
            const c = dist[n] || 0;
            const pct = Math.round((c / maxBar) * 100);
            return `
              <div class="ysr-dist-row">
                <span class="ysr-dist-label">${n} ★</span>
                <span class="ysr-dist-track"><span class="ysr-dist-fill" style="width:${pct}%"></span></span>
                <span class="ysr-dist-count">${c}</span>
              </div>`;
          })
          .join('');

        return `
          <div class="ysr-summary">
            <div class="ysr-summary__score">
              <span class="ysr-summary__avg">${avg}</span>
              ${this.#stars(b.averageScore || 0)}
              <span class="ysr-summary__total">Based on ${b.totalReview || 0} review${b.totalReview === 1 ? '' : 's'}</span>
            </div>
            <div class="ysr-summary__dist">${bars}</div>
          </div>`;
      }

      #renderCard(r) {
        const product = this.#tab.products[r.product_id];
        const productName = product ? `<span class="ysr-card__product">on ${this.#escape(product.name)}</span>` : '';
        const verified = r.verified_buyer
          ? `<span class="ysr-card__verified">${this.#verifiedSVG()} Verified Buyer</span>`
          : '';
        const name = r.user?.display_name || r.name || 'Anonymous';

        return `
          <li class="ysr-card">
            <div class="ysr-card__avatar" aria-hidden="true">${this.#initial(name)}</div>
            <div class="ysr-card__body">
              <div class="ysr-card__head">
                ${this.#stars(r.score || 0)}
                <span class="ysr-card__date">${this.#formatDate(r.created_at)}</span>
              </div>
              ${r.title ? `<h3 class="ysr-card__title">${this.#escape(r.title)}</h3>` : ''}
              ${r.content ? `<p class="ysr-card__content">${this.#escape(r.content)}</p>` : ''}
              <div class="ysr-card__meta">
                <span class="ysr-card__author">${this.#escape(name)}</span>
                ${verified}
                ${productName}
              </div>
            </div>
          </li>`;
      }

      #renderLoadMore() {
        const s = this.#tab;
        if (s.reviews.length >= s.total) return '';
        const label = s.loading ? 'Loading…' : 'Load more reviews';
        return `<div class="ysr-loadmore"><button type="button" class="ysr-loadmore__btn" data-load-more${s.loading ? ' disabled' : ''}>${label}</button></div>`;
      }

      #verifiedSVG() {
        return '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true"><path d="M6.5 13C10.0899 13 13 10.0899 13 6.5C13 2.91015 10.0899 0 6.5 0C2.91015 0 0 2.91015 0 6.5C0 10.0899 2.91015 13 6.5 13Z" fill="#709525"/><path d="M3.4668 6.25919L5.82646 8.6666L9.9668 4.7666" stroke="white" stroke-width="1.3"/></svg>';
      }

      #renderSkeleton() {
        const cards = Array.from({ length: 4 })
          .map(() => '<li class="ysr-card ysr-card--skeleton"><div class="ysr-skel-line"></div><div class="ysr-skel-line ysr-skel-line--short"></div><div class="ysr-skel-line"></div></li>')
          .join('');
        return `<div class="ysr-summary ysr-summary--skeleton"></div><ul class="ysr-list">${cards}</ul>`;
      }
    }
  );
}
